import React, { ReactElement } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AccountTypeEnum, AccountTypeFromBankEnum } from "@rentablo/api/apiv1";
import { ContraAccountRow } from "./ContraAccountRow";
import { OptionRow } from "./OptionRow";

export interface GroupedAccountListItem {
  id: number;
  bankName?: string;
  accountName?: string;
  iban?: string;
  bankImage?: string;
  type?: AccountTypeFromBankEnum;
  accountType?: AccountTypeEnum;
}

interface Props {
  items: GroupedAccountListItem[];
  isSelected: (id: number) => boolean;
  // The row is a <label>, so a click anywhere in it activates this control —
  // selection must be handled here only, never also on the row itself.
  renderControl: (id: number, selected: boolean) => ReactElement;
}

const groupByBank = (
  items: GroupedAccountListItem[],
  fallbackLabel: string
): [string, GroupedAccountListItem[]][] => {
  const map = new Map<string, GroupedAccountListItem[]>();
  for (const item of items) {
    const key = item.bankName ?? fallbackLabel;
    const group = map.get(key) ?? [];
    group.push(item);
    map.set(key, group);
  }
  return Array.from(map.entries());
};

export const GroupedAccountList = ({
  items,
  isSelected,
  renderControl,
}: Props) => {
  const { t } = useTranslation();
  const groups = groupByBank(
    items,
    t("createAndEditAccount.additionalContraAccounts.otherBank")
  );

  if (groups.length === 0) {
    return (
      <Typography
        variant="body4"
        color="textTertiary"
        display="block"
        py={3}
        data-testid="grouped-account-list-empty"
      >
        {t("createAndEditAccount.additionalContraAccounts.noOptions")}
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {groups.map(([bankName, groupItems]) => (
        <Box key={bankName} display="flex" flexDirection="column">
          <Typography
            variant="overline"
            color="textTertiary"
            display="block"
            mb={1}
          >
            {bankName}
          </Typography>
          {groupItems.map((item) => (
            <OptionRow
              key={item.id}
              isSelected={isSelected(item.id)}
              control={renderControl(item.id, isSelected(item.id))}
              label={
                <ContraAccountRow
                  accountName={item.accountName}
                  iban={item.iban}
                  bankImage={item.bankImage}
                  type={item.type}
                  accountType={item.accountType}
                />
              }
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};
